import { useQuery } from "@tanstack/react-query";
import apiClient from "./apiClient";
import { useAppContext } from "../libs/appContextLib";
import { getRecipesApiUrl } from "./recipesApiHelpers";

/**
 * Fetch all recipes for the current user
 *
 * @param {Object} options - Optional filter parameters
 * @param {string} [options.status] - Filter by status: 'draft' | 'active' | 'archived'
 * @param {string} [options.category] - Filter by category
 * @param {boolean} [options.enabled] - Enable/disable the query (default: true)
 * @returns {Object} React Query result with recipes data
 */
export function useFetchRecipes({ status, category, enabled = true } = {}) {
    const { currUser } = useAppContext();

    return useQuery({
        queryKey: [`${currUser.customerId}_recipes-list`, status, category],
        queryFn: async () => {
            const params = {};
            if (status) params.status = status;
            if (category) params.category = category;

            const response = await apiClient.get(
                `${getRecipesApiUrl()}/recipes/list`,
                { params }
            );
            return response.data;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes (was 10 seconds - too aggressive)
        gcTime: 1000 * 60 * 30, // 30 minutes
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        enabled: !!currUser?.customerId && enabled, // Combine both conditions
    });
}
